import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';

// libphonenumber singleton — handles the heavy lifting of parsing,
// validating and formatting phone numbers in every UI path. We never
// strip leading zeros or concatenate dial codes by hand; the library
// does it correctly for every region including trunk-prefix locales.
const phoneUtil = PhoneNumberUtil.getInstance();

// Country code to dial code mapping — kept ONLY for building the
// country selector labels. Never use this for parsing or formatting;
// always go through libphonenumber.
export const countryCodeToDialCode: Record<string, string> = {
  'PY': '+595',
  'AR': '+54',
  'BR': '+55',
  'CL': '+56',
  'CO': '+57',
  'EC': '+593',
  'PE': '+51',
  'UY': '+598',
  'VE': '+58',
  'BO': '+591',
  'MX': '+52',
  'US': '+1',
  'ES': '+34',
  'DE': '+49',
  'FR': '+33',
  'GB': '+44',
  'IT': '+39',
  'PT': '+351',
};

export const countryOptions = [
  { code: 'PY', name: 'Paraguay (+595)', flag: '🇵🇾' },
  { code: 'AR', name: 'Argentina (+54)', flag: '🇦🇷' },
  { code: 'BR', name: 'Brasil (+55)', flag: '🇧🇷' },
  { code: 'CL', name: 'Chile (+56)', flag: '🇨🇱' },
  { code: 'CO', name: 'Colombia (+57)', flag: '🇨🇴' },
  { code: 'EC', name: 'Ecuador (+593)', flag: '🇪🇨' },
  { code: 'PE', name: 'Perú (+51)', flag: '🇵🇪' },
  { code: 'UY', name: 'Uruguay (+598)', flag: '🇺🇾' },
  { code: 'VE', name: 'Venezuela (+58)', flag: '🇻🇪' },
  { code: 'BO', name: 'Bolivia (+591)', flag: '🇧🇴' },
  { code: 'MX', name: 'México (+52)', flag: '🇲🇽' },
  { code: 'US', name: 'Estados Unidos (+1)', flag: '🇺🇸' },
  { code: 'ES', name: 'España (+34)', flag: '🇪🇸' },
  { code: 'DE', name: 'Alemania (+49)', flag: '🇩🇪' },
  { code: 'FR', name: 'Francia (+33)', flag: '🇫🇷' },
  { code: 'GB', name: 'Reino Unido (+44)', flag: '🇬🇧' },
  { code: 'IT', name: 'Italia (+39)', flag: '🇮🇹' },
  { code: 'PT', name: 'Portugal (+351)', flag: '🇵🇹' },
];

interface PhoneInputProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  showLabel?: boolean;
  id?: string;
}

/**
 * Parse a national-format phone number against an ISO region and return
 * the E.164 international number (with the leading `+`). Delegates
 * 100% of the trunk-prefix / dial-code logic to libphonenumber.
 * Returns null if the number can't be parsed or isn't valid.
 */
export function formatPhoneWithCountryCode(countryCode: string, phoneNumber: string): string {
  if (!phoneNumber || !countryCode) return phoneNumber;
  try {
    const parsed = phoneUtil.parse(phoneNumber, countryCode);
    if (!phoneUtil.isValidNumber(parsed)) return phoneNumber;
    return phoneUtil.format(parsed, PhoneNumberFormat.E164);
  } catch {
    // Not parseable yet — the user is still typing. Return input as-is
    // so the controlled input doesn't swallow keystrokes.
    return phoneNumber;
  }
}

/** Digits-only international number for API/DB (e.g. 595991123456). */
export function toInternationalPhoneDigits(countryCode: string, phoneNumber: string): string {
  if (!phoneNumber || !countryCode) return (phoneNumber || '').replace(/\D/g, '');
  try {
    const parsed = phoneUtil.parse(phoneNumber, countryCode);
    if (!phoneUtil.isValidNumber(parsed)) return phoneNumber.replace(/\D/g, '');
    return phoneUtil.format(parsed, PhoneNumberFormat.E164).replace(/\D/g, '');
  } catch {
    return phoneNumber.replace(/\D/g, '');
  }
}

/**
 * True when the national-format input is a valid number for the given
 * ISO region. Delegates to libphonenumber's isValidNumber — used by
 * forms to gate submission. Returns false on unparseable input.
 */
export function isValidPhone(countryCode: string, phoneNumber: string): boolean {
  if (!phoneNumber || !countryCode) return false;
  try {
    return phoneUtil.isValidNumber(phoneUtil.parse(phoneNumber, countryCode));
  } catch {
    return false;
  }
}

/**
 * Reverse operation: given a stored E.164 digits string (e.g.
 * "595981123456" or "+595981123456"), return the ISO region and the
 * national-format number suitable for the PhoneInput fields.
 *
 * Delegates the entire split to libphonenumber — we never hand-match
 * dial-code prefixes.
 */
export function splitE164ForDisplay(
  e164OrDigits: string | null | undefined,
): { countryCode: string; nationalNumber: string } {
  const trimmed = String(e164OrDigits ?? '').trim();
  if (!trimmed) return { countryCode: 'PY', nationalNumber: '' };
  const withPlus = trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/\D/g, '')}`;
  try {
    const parsed = phoneUtil.parse(withPlus);
    const region = phoneUtil.getRegionCodeForNumber(parsed) || 'PY';
    const national = phoneUtil
      .format(parsed, PhoneNumberFormat.NATIONAL)
      .replace(/[^\d\s]/g, '')
      .trim();
    if (national) return { countryCode: region, nationalNumber: national };
  } catch {
    /* fall through to manual strip */
  }
  // Fallback: strip a known dial code off the front so we never
  // pre-fill an edit form with the raw international digits.
  const digits = trimmed.replace(/\D/g, '');
  for (const [iso, dial] of Object.entries(countryCodeToDialCode)) {
    const dialDigits = dial.replace(/\D/g, '');
    if (digits.startsWith(dialDigits) && digits.length > dialDigits.length) {
      return { countryCode: iso, nationalNumber: digits.slice(dialDigits.length) };
    }
  }
  return { countryCode: 'PY', nationalNumber: digits };
}

/**
 * Format a stored phone (E.164 digits or any international-ish
 * string) for DISPLAY to end users. Always returns the national
 * format of the region libphonenumber detects, so the UI never
 * shows raw "595981123456" to the user.
 *
 * Use this wherever a stored phone is rendered on screen (contact
 * details, inbox header, task cards, campaigns preview, etc.).
 */
export function formatPhoneForDisplay(
  e164OrDigits: string | null | undefined,
): string {
  const trimmed = String(e164OrDigits ?? '').trim();
  if (!trimmed) return '';
  const withPlus = trimmed.startsWith('+') ? trimmed : `+${trimmed.replace(/\D/g, '')}`;
  try {
    const parsed = phoneUtil.parse(withPlus);
    // Always try to format, even if isValidNumber() is strict —
    // WhatsApp/Meta already validated the number by pairing, and
    // returning the raw E.164 to the user is never acceptable.
    const national = phoneUtil.format(parsed, PhoneNumberFormat.NATIONAL);
    if (national && /\d/.test(national)) return national;
  } catch {
    /* fall through to the manual strip fallback */
  }
  // Fallback: strip a known dial code prefix and add a local
  // trunk zero so the user at least never sees the +country digits.
  const digits = trimmed.replace(/\D/g, '');
  for (const [, dial] of Object.entries(countryCodeToDialCode)) {
    const dialDigits = dial.replace(/\D/g, '');
    if (digits.startsWith(dialDigits) && digits.length > dialDigits.length) {
      const national = digits.slice(dialDigits.length);
      return national.length >= 7 ? `0${national}` : national;
    }
  }
  return digits;
}

export default function PhoneInput({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  label = 'Phone Number',
  placeholder = 'Enter phone number',
  required = false,
  disabled = false,
  showLabel = true,
  id = 'phone'
}: PhoneInputProps) {
  return (
    <div className="space-y-2">
      {showLabel && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      <div className="flex gap-2">
        <Select
          value={countryCode}
          onValueChange={onCountryCodeChange}
          disabled={disabled}
        >
          <SelectTrigger className="w-[110px] flex-shrink-0">
            <SelectValue placeholder="País">
              {countryCode ? (
                <span className="flex items-center gap-1.5">
                  <span>{countryOptions.find((c) => c.code === countryCode)?.flag}</span>
                  <span>{countryCodeToDialCode[countryCode]}</span>
                </span>
              ) : null}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {countryOptions.map((country) => (
              <SelectItem key={country.code} value={country.code}>
                <span className="flex items-center gap-2">
                  <span>{country.flag}</span>
                  <span className="font-mono text-muted-foreground">{countryCodeToDialCode[country.code]}</span>
                  <span>{country.name.replace(/\s*\(\+?\d+\)\s*$/, '')}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          id={id}
          type="tel"
          value={phoneNumber}
          onChange={(e) => onPhoneNumberChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className="flex-1"
        />
      </div>
    </div>
  );
}
